import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';
import { loadGoogleAnalytics } from '../utils/analytics';

export const STORAGE_KEY = 'primeslink_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Slight delay so the page loads first
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    } else {
      const parsed = JSON.parse(stored);
      if (parsed.analytics) loadGoogleAnalytics();
    }
  }, []);

  const save = (prefs) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefs, timestamp: Date.now() }));
    if (prefs.analytics) loadGoogleAnalytics();
    setVisible(false);
  };

  const acceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true };
    setPreferences(all);
    save(all);
  };

  const rejectNonEssential = () => {
    const minimal = { necessary: true, analytics: false, marketing: false };
    setPreferences(minimal);
    save(minimal);
  };

  const saveCustom = () => {
    save(preferences);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-dark-900 border border-white/15 rounded-2xl shadow-2xl p-6">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <Cookie size={22} className="text-primary-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <h3 className="text-white font-semibold text-base">We use cookies</h3>
                <p className="text-gray-400 text-sm mt-1">
                  We use cookies to enhance your experience, analyze site traffic, and show relevant content. You can choose which cookies to allow.{' '}
                  <a
                    href="/privacy-policy"
                    className="text-primary-400 hover:underline cursor-pointer"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
              <button
                onClick={rejectNonEssential}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors shrink-0"
                aria-label="Dismiss"
              >
                <X size={16} className="text-gray-400" />
              </button>
            </div>

            {/* Expandable Details */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="grid gap-3 pt-2">
                    {/* Necessary */}
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                      <div>
                        <p className="text-white text-sm font-medium">Necessary</p>
                        <p className="text-gray-400 text-xs mt-0.5">Required for the site to function. Cannot be disabled.</p>
                      </div>
                      <div className="w-10 h-5 bg-primary-500 rounded-full relative shrink-0">
                        <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow" />
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                      <div>
                        <p className="text-white text-sm font-medium">Analytics</p>
                        <p className="text-gray-400 text-xs mt-0.5">Help us understand how visitors use the site.</p>
                      </div>
                      <button
                        onClick={() => setPreferences((p) => ({ ...p, analytics: !p.analytics }))}
                        className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ${preferences.analytics ? 'bg-primary-500' : 'bg-white/20'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${preferences.analytics ? 'right-0.5' : 'left-0.5'}`} />
                      </button>
                    </div>

                    {/* Marketing */}
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                      <div>
                        <p className="text-white text-sm font-medium">Marketing</p>
                        <p className="text-gray-400 text-xs mt-0.5">Used to show you relevant ads and content.</p>
                      </div>
                      <button
                        onClick={() => setPreferences((p) => ({ ...p, marketing: !p.marketing }))}
                        className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ${preferences.marketing ? 'bg-primary-500' : 'bg-white/20'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${preferences.marketing ? 'right-0.5' : 'left-0.5'}`} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 items-center">
              <button
                onClick={acceptAll}
                className="px-5 py-2 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-shadow cursor-pointer"
              >
                Accept All
              </button>
              <button
                onClick={rejectNonEssential}
                className="px-5 py-2 border border-white/20 text-gray-300 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors cursor-pointer"
              >
                Necessary Only
              </button>
              {showDetails && (
                <button
                  onClick={saveCustom}
                  className="px-5 py-2 border border-primary-500/50 text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-500/10 transition-colors cursor-pointer"
                >
                  Save Preferences
                </button>
              )}
              <button
                onClick={() => setShowDetails((v) => !v)}
                className="ml-auto flex items-center gap-1 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"
              >
                {showDetails ? 'Hide' : 'Manage'} preferences
                {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Exported helper so other parts of the app can re-open cookie settings
export function reopenCookieBanner() {
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}
